import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {TimelineLite} from "gsap";
import {BasicBitmapCountUp} from "../component/BasicBitmapCountUp";
import {RoadKill} from "../RoadKill";
import {RoadKillGameAssets} from "../RoadKillGameAssets";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {BelowStakeWinRestriction} from "../server/BelowStakeWinRestriction";
import {SimplePresentationRunnerController} from "../../core/winpresentation/SimplePresentationRunner";
import {RoadKillGameConfig} from "../RoadKillGameConfig";
import {RoadKillSound} from "../sound/RoadKillSound";
import {RoadKillGameMode} from "../server/RoadKillGameMode";

export class RoadKillWinCapView extends PIXI.Container implements StageLayer {
    private _overlay: PIXI.Sprite;
    private _countUp: BasicBitmapCountUp;
    private _button: ContinueButton;
    private _winTextSprite: PIXI.Sprite;
    private _spine: PIXI.spine.Spine;
    private _flowStateController: SimplePresentationRunnerController;

    constructor() {
        super();
        this.name = "winCapView";
        this.init();
        this.onResize();
    }

    public show(flowStateController: SimplePresentationRunnerController): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.add(() => {
            this._overlay.visible = true;
            this._countUp.visible = true;
            //this._winText.visible = true;
            this._countUp.getCountAnimation(0, 0, 0);
            this._flowStateController = flowStateController;
            RoadKill.bigWinView.playBigWin(flowStateController, true).add(() => {
                RoadKill.particleManager.bigWinParticles.enableEmit(false);
                RoadKill.particleManager.bigWinParticles2.enableEmit(false);
                RoadKill.particleManager.bigWinParticles3.enableEmit(false);
                this.onBigWinEnd();
            })
        });

        return tl;
    }

    public onReelStart(): TimelineLite {
        if (!this._countUp.visible) {
            return new TimelineLite();
        }
        const tl: TimelineLite = new TimelineLite({
            onComplete: () => {
                this._countUp.visible = false;
            }
        });
        tl.to(this._countUp, {alpha: 0, duration: RoadKillGameConfig.HIDE_AND_ALPHA_DURATION});
        return tl;
    }

    public onResize(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;
        this._overlay.width = NolimitApplication.screenBounds.width;
        this._overlay.height = NolimitApplication.screenBounds.height;
        this._overlay.x = NolimitApplication.screenBounds.left;
        this._overlay.y = NolimitApplication.screenBounds.top;
        if (NolimitApplication.isLandscape) {
            this._button.position.set(360, 615);
        } else {
            const centerBottom = (bounds.bottom - 610) * 0.5 + 610;
            this._button.position.set(360, centerBottom - 80);
        }
    }

    private onBigWinEnd(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._countUp.visible = true;
        this._button.visible = true;
        this._winTextSprite.visible = true;
        this._spine.alpha = 0;
        this._spine.visible = true;
        tl.add(() => {
            RoadKill.soundPlayer.playTrack(RoadKillSound.WIN_CAP_START_ONE_SHOT);
        });
        tl.addLabel("countUp_Overlay"); //need to be removed once we get correct data from server
        tl.to(this._overlay, {alpha: 0.5, duration: 0.3}, "countUp_Overlay");
        tl.to(this._spine, {
            alpha: 1, duration: 0.3, onStart: () => {
                this._spine.state.setAnimation(0, "Win_Cap_Start", false);
            }
        }, "countUp_Overlay");
        tl.to(this._countUp, {alpha: 1, duration: 0.3}, "countUp_Overlay");
        tl.add(() => {
            this._spine.state.setAnimation(0, "Win_Cap", true);
        });

        tl.add([this._countUp.getCountAnimation(0, RoadKill.model.data.accumulatedRoundWin, 5, "power3.in"), () => {
            RoadKill.soundPlayer.playTrack(RoadKillSound.WIN_CAP_COUNT_UP, 1, Number.MAX_VALUE);
        }]);
        tl.add(() => {
            RoadKill.soundPlayer.playTrack(RoadKillSound.WIN_CAP_LOOP, 1, Number.MAX_VALUE);
            RoadKill.soundPlayer.stopTrack(RoadKillSound.WIN_CAP_COUNT_UP);
        })
        tl.to(this._winTextSprite.scale, {x: 1, y: 1, duration: 0.3}, "<");
        tl.add(() => {
            this._spine.state.setAnimation(0, "Win_Cap_End", true);
        });
        tl.to([this._winTextSprite, this._button], {alpha: 1, duration: 0.3}, "<");
        tl.to(this, {duration: 0.5});
        tl.to(this._countUp, {alpha: 0, duration: 0.3});
        tl.add(() => {
            RoadKill.keypadPlugin.setWin(RoadKill.model.data.accumulatedRoundWin, true, BelowStakeWinRestriction.isWinBelowStake());
            this._button.enable(true);
        });
        return tl;
    }

    private createSpine(): PIXI.spine.Spine {
        const spine: PIXI.spine.Spine = new PIXI.spine.Spine(RoadKill.assetLoader.getSpineAsset("winCap"));
        spine.position.set(360); //y310
        spine.visible = false;
        return spine;
    }

    private init(): void {
        this._overlay = this.createOverlay();
        this._countUp = this.createCountUp();
        this._button = this.createButton();
        this._winTextSprite = this.createWinTextSprite();
        this._spine = this.createSpine();
        this.addChild(this._overlay, this._spine, this._countUp, this._winTextSprite, this._button);
    }

    private createOverlay(): PIXI.Sprite {
        const overlay: PIXI.Sprite = new PIXI.Sprite(PIXI.Texture.WHITE);
        overlay.tint = 0x000000;
        overlay.alpha = 0;
        return overlay;
    }

    private createCountUp(): BasicBitmapCountUp {
        const countUp = new BasicBitmapCountUp({
            fontName: RoadKillGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 132,
            tint: 0xffffff
        }, 720);
        countUp.scale.set(0.8, 0.8);
        countUp.stopAt(0);
        countUp.position.set(360, 360);
        countUp.visible = false;
        countUp.alpha = 0;
        return countUp;
    }

    private createButton(): ContinueButton {
        const button = new ContinueButton();
        button.addClickCallback(() => this.onButtonClick());
        button.pivot.set(button.width * 0.5, button.height * 0.5);
        button.enable(false)
        button.alpha = 0;
        return button;
    }

    private onButtonClick(): TimelineLite {
        const hideTl: TimelineLite = new TimelineLite({
            onComplete: () => {
                this._overlay.visible = false;
                this._winTextSprite.visible = false;
                this._winTextSprite.scale.set(0);
                this._button.visible = false;
                this._spine.visible = false;
                this._spine.state.setEmptyAnimation(0, 0);
                RoadKill.soundPlayer.fadeMusic(1, 300);
            }
        });
        RoadKill.soundPlayer.playTrack(RoadKillSound.WIN_CAP_END);
        this._flowStateController.finish();
        this._button.enable(false);
        hideTl.to([this._overlay, this._winTextSprite, this._button, this._spine], {alpha: 0, duration: 0.3});
        hideTl.add(() => {
            RoadKill.soundPlayer.stopTrack(RoadKillSound.WIN_CAP_LOOP);
            RoadKill.soundPlayer.switchMusic(RoadKillGameMode.NORMAL);
            RoadKill.soundPlayer.fadeMusic(1, 400);
        })
        return hideTl;
    }

    private createWinTextSprite(): PIXI.Sprite {
        const text = new PIXI.Sprite(RoadKill.assetLoader.getTextures("kingOfTheRoad")[0]);
        text.anchor.set(0.5);
        text.visible = false;
        text.alpha = 0;
        text.scale.set(0);
        text.position.set(360, 500);
        return text;
    }
}